// src/hooks/useBreakpoint.ts
import { useState, useEffect } from 'react';

import { Breakpoint, breakpointPxMap, getCurrentBreakpointValue } from '../utils/breakpoint';

/**
 * Хук для реактивного получения текущего активного брейкпоинта Tailwind CSS.
 * Пересчитывает брейкпоинт каждый раз, когда размер окна меняется и пересекает границу брейкпоинта.
 * @returns {Breakpoint} Текущий брейкпоинт в виде числового значения (например, 768 для 'md').
 */
export const useBreakpoint = (): Breakpoint => {
  // Инициализируем состояние текущим брейкпоинтом
  const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>(getCurrentBreakpointValue());

  useEffect(() => {
    // Функция, которая будет вызываться при изменении размера окна или брейкпоинта
    const handleResize = () => {
      // Обновляем состояние только если брейкпоинт действительно изменился
      const newBreakpoint = getCurrentBreakpointValue();
      if (newBreakpoint !== currentBreakpoint) {
        setCurrentBreakpoint(newBreakpoint);
      }
    };

    // Создаём объекты MediaQueryList для каждого брейкпоинта и подписываемся на их изменения
    const mediaQueryLists: MediaQueryList[] = Object.values(breakpointPxMap).map((bp) => {
      const mql = window.matchMedia(`(min-width: ${bp})`);
      mql.addEventListener('change', handleResize); // Слушаем событие 'change'

      return mql;
    });

    // Также добавляем слушатель на 'resize' окна.
    // Это важно, так как 'change' на MediaQueryList может не срабатывать достаточно часто,
    // если окно изменяется очень быстро или если вы хотите более точное отслеживание
    // даже внутри одного брейкпоинта (хотя для брейкпоинтов это менее критично).
    window.addEventListener('resize', handleResize);

    // Функция очистки (cleanup function):
    // Вызывается, когда компонент размонтируется или когда зависимости useEffect меняются.
    // Удаляем все слушатели, чтобы избежать утечек памяти.
    return () => {
      mediaQueryLists.forEach((mql) => mql.removeEventListener('change', handleResize));
      window.removeEventListener('resize', handleResize);
    };
  }, [currentBreakpoint]); // Добавляем currentBreakpoint в зависимости, чтобы handleResize всегда использовал актуальное значение.

  return currentBreakpoint;
};
